import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { EmailAnalysisParticipantSide, EmailAnalysisTabType } from "@types";
import { env } from "@config";
import { getCurrentContextEmailProps } from "@utils";
import { useAppDispatch, useAppSelector } from "@store";
import { switchAnalysisTab, analyzeThread, selectEmail, updateThreadId } from "@store/analysis";
import { getThreadTrackStatus, updateThreadTrackStatus } from "@store/signals";
import { useMixpanel } from "@taskpane/context";
import { withTransition } from "@taskpane/hoc";
import {
  CheckboxCheckedGreenIcon,
  CheckboxIcon,
  LinkBlueIcon,
  LinkIcon,
  RefreshIcon,
} from "@taskpane/icons";
import {
  AnalysisSummary,
  AuthorityZone,
  AvatarVariant,
  Button,
  ButtonSize,
  ButtonVariant,
  ErrorOverlay,
  Header,
  MessageAnalysis,
  NextMoveAnalysis,
  Scrollbar,
  SentimentAnalysis,
  Tabs,
  UpperHandAnalysis,
  XRayAnalysis,
} from "@taskpane/components";
import { useContentHeight } from "@taskpane/hooks";
import { EmptyScreen } from "../EmptyScreen";
import css from "./AnalysisScreen.module.scss";

const AnalysisScreen: React.FC = () => {
  const dispatch = useAppDispatch();

  const isComposeMode = useAppSelector((state) => state.global.isComposeMode);
  const user = useAppSelector((state) => state.auth.user);
  const result = useAppSelector((state) => state.analysis.result);
  const activeTabId = useAppSelector((state) => state.analysis.activeTabId);
  const selectedBranch = useAppSelector((state) => state.analysis.selectedBranch);
  const selectedEmail = useAppSelector((state) => state.analysis.selectedEmail);
  const isLoadingValue = useAppSelector((state) => state.analysis.isLoading.analyzeThread);
  const isError = useAppSelector((state) => state.analysis.isError.analyzeThread);
  const isPermissionError = useAppSelector((state) => state.analysis.isError.permissionError);
  const isTrackStatusLoading = useAppSelector((state) => state.signals.isLoading.threadTrackStatus);
  const threadId = useAppSelector((state) => state.analysis.threadId);
  const threadTrackStatus = useAppSelector((state) => state.analysis.threadTrackStatus);
  const isLoading = !result || isLoadingValue;

  const { trackEvent } = useMixpanel();

  const currentAnalyzeRequestRef = useRef<
    | (Promise<unknown> & {
        abort: () => void;
      })
    | null
  >(null);

  const headerWrapperRef = useRef<HTMLDivElement>(null);
  const contentWrapperRef = useRef<HTMLDivElement>(null);

  const tabs = useMemo(
    () => [
      {
        id: EmailAnalysisTabType.Message,
        label: "Message",
        tooltip: "Review related emails in the conversation for quick insights and easy access",
      },
      {
        id: EmailAnalysisTabType.Analysis,
        label: "Analysis",
        tooltip: "View the power dynamics between you and your prospect at any point in the thread",
      },
      {
        id: EmailAnalysisTabType.XRay,
        label: "X-Ray",
        showIndicator: !!selectedEmail?.xRayAnalytics?.length,
        tooltip: "Uncover non verbal signals in the analyzed message",
      },
      {
        id: EmailAnalysisTabType.NextMove,
        label: "Next Move",
        tooltip: "Get guidance on your next move to advance the conversation",
      },
    ],
    [selectedEmail]
  );

  const xRayAnalysisItems = useMemo(
    () =>
      selectedEmail?.xRayAnalytics?.map(
        ({ positiveUhDirection, token, textBefore, textAfter }) => ({
          textBefore,
          token,
          textAfter,
          isPositive: positiveUhDirection,
        })
      ) || [],
    [selectedEmail]
  );

  const headerOptions = useMemo(
    () => [
      {
        id: "trackStatus",
        label: "Track",
        icon: threadTrackStatus ? CheckboxCheckedGreenIcon : CheckboxIcon,
        disabled: threadTrackStatus === null || isTrackStatusLoading,
        tooltip: threadTrackStatus
          ? "Disable tracking of email opens and participant changes."
          : "Enable tracking of email opens and participant changes.",
        onClick: () => {
          dispatch(updateThreadTrackStatus({ threadId, onTrack: !threadTrackStatus }));
        },
      },
    ],
    [dispatch, threadTrackStatus, isTrackStatusLoading]
  );

  const handleAnalyzeThread = useCallback(async () => {
    if (isComposeMode || result || !user) {
      return;
    }

    const { conversationId, messageId, outlookProfileEmail, participants, subject } =
      await getCurrentContextEmailProps();

    if (conversationId && messageId) {
      dispatch(updateThreadId(conversationId));
      currentAnalyzeRequestRef.current?.abort();
      currentAnalyzeRequestRef.current = dispatch(
        analyzeThread({
          conversationId,
          messageId,
          outlookProfileEmail,
          participants,
          subject,
        })
      );
    }
  }, [isComposeMode, result, user, dispatch]);

  const attrs = {
    container: {
      className: css.container,
    },
    headerWrapper: {
      ref: headerWrapperRef,
      className: css.headerWrapper,
    },
    emptyScreen: {
      text: (
        <>
          Open an email thread
          <br />
          and click Analyze to proceed
        </>
      ),
    },
    header: {
      isLoading,
      title: "Analysis",
      options: headerOptions,
    },
    authorityZone: {
      isLoading,
      selectedBranch,
      selectedEmail,
      branches: result?.branchResults || [],
      nextMove: selectedBranch?.nextMove?.value,
      onSelectEmail: (messageId: string) => {
        dispatch(selectEmail(messageId));
        dispatch(switchAnalysisTab(EmailAnalysisTabType.Message));
      },
      onSelectNextMove: () => {
        dispatch(switchAnalysisTab(EmailAnalysisTabType.NextMove));
      },
    },
    tabsWrapper: {
      className: css.tabsWrapper,
    },
    tabs: {
      tabs,
      activeTabId,
      disabled: isLoading,
      onTabClick: (tabId: EmailAnalysisTabType) => {
        trackEvent(`analysisScreen.${tabId}TabClick`);
        dispatch(switchAnalysisTab(tabId));
      },
    },
    contentWrapper: {
      ref: contentWrapperRef,
      className: css.contentWrapper,
    },
    scrollbar: {
      className: css.scrollbar,
    },
    content: {
      className: css.content,
    },
    messageAnalysis: {
      isLoading,
      currentIndex: selectedEmail?.index,
      sender:
        selectedEmail?.sender === EmailAnalysisParticipantSide.Left
          ? selectedEmail?.participants?.leftParticipant
          : selectedEmail?.participants?.rightParticipant,
      text: selectedEmail?.html || null,
      totalCount: selectedBranch?.emails?.length || 0,
    },
    upperHandAnalysis: {
      isLoading,
      dominantSide: selectedEmail?.dominantParticipant || "unresolved",
      leftParticipant: {
        email: selectedEmail?.participants?.leftParticipant?.email || "",
        name:
          selectedEmail?.participants?.leftParticipant?.metadata?.name ||
          selectedEmail?.participants?.leftParticipant?.email ||
          "",
        avatarProps: {
          src: selectedEmail?.participants?.leftParticipant?.metadata?.avatarSrc || "",
          name:
            selectedEmail?.participants?.leftParticipant?.metadata?.name ||
            selectedEmail?.participants?.leftParticipant?.email ||
            "",
          variant: selectedEmail?.participants?.leftParticipant?.isUser
            ? AvatarVariant.User
            : AvatarVariant.Prospect,
          ...(isLoading && {
            variant: AvatarVariant.Default,
          }),
        },
      },
      rightParticipant: {
        email: selectedEmail?.participants?.rightParticipant?.email || "",
        name:
          selectedEmail?.participants?.rightParticipant?.metadata?.name ||
          selectedEmail?.participants?.rightParticipant?.email ||
          "",
        avatarProps: {
          src: selectedEmail?.participants?.rightParticipant?.metadata?.avatarSrc || "",
          name:
            selectedEmail?.participants?.rightParticipant?.metadata?.name ||
            selectedEmail?.participants?.rightParticipant?.email ||
            "",
          variant: selectedEmail?.participants?.rightParticipant?.isUser
            ? AvatarVariant.User
            : AvatarVariant.Prospect,
          ...(isLoading && {
            variant: AvatarVariant.Default,
          }),
        },
      },
    },
    sentimentAnalysis: {
      isLoading,
      value: selectedEmail?.sentiment?.mag || null,
      name:
        selectedEmail?.sender === EmailAnalysisParticipantSide.Left
          ? selectedEmail?.participants?.leftParticipant?.metadata?.name || ""
          : selectedEmail?.participants?.rightParticipant?.metadata?.name || "",
    },
    analysisSummary: {
      isLoading,
      items: selectedEmail?.summary || [],
    },
    openAnalysisButton: {
      disabled: isLoading,
      variant: ButtonVariant.Secondary,
      size: ButtonSize.S,
      onClick: () => {
        if (user && selectedBranch) {
          trackEvent("analysisScreen.openAnalysisClick");
          window.open(
            `${env.accountOrigin}/orgs/${user.orgId}/users/${user.userId}/touch-points/${(selectedBranch.branchId || "").replace(/-draft.*/, "")}`,
            "_blank",
            "noopener,noreferrer"
          );
        }
      },
    },
    nextMoveAnalysis: {
      isLoading,
      items: selectedBranch?.nextMove?.value ? [selectedBranch.nextMove.value] : [],
    },
    xRayAnalysis: {
      isLoading,
      items: xRayAnalysisItems,
    },
    errorOverlay: {
      isOpened: isError,
      buttonIcon: isPermissionError ? LinkIcon : RefreshIcon,
      description: isPermissionError
        ? "Connect your Microsoft account to continue viewing your account data"
        : "Please refresh to try again",
      title: "Analysis error",
      buttonText: isPermissionError ? "Continue" : "Refresh",
      onClick: () => {
        if (isPermissionError) {
          window.open(
            `${env.accountOrigin}/orgs/${user?.orgId}/users/${user?.userId}`,
            "_blank",
            "noopener,noreferrer"
          );
        } else {
          handleAnalyzeThread();
        }
      },
    },
  };

  const renderActiveTabContent = () => {
    if (activeTabId === EmailAnalysisTabType.Message) {
      return <MessageAnalysis {...attrs.messageAnalysis} />;
    }

    if (activeTabId === EmailAnalysisTabType.Analysis) {
      return (
        <>
          <UpperHandAnalysis {...attrs.upperHandAnalysis} />
          <SentimentAnalysis {...attrs.sentimentAnalysis} />
          <AnalysisSummary {...attrs.analysisSummary} />
          <Button {...attrs.openAnalysisButton}>
            <span>Open full analysis</span>
            <LinkBlueIcon />
          </Button>
        </>
      );
    }

    if (activeTabId === EmailAnalysisTabType.XRay) {
      return <XRayAnalysis {...attrs.xRayAnalysis} />;
    }

    if (activeTabId === EmailAnalysisTabType.NextMove) {
      return <NextMoveAnalysis {...attrs.nextMoveAnalysis} />;
    }

    return null;
  };

  useEffect(() => {
    trackEvent("analysisScreen.view");
  }, [trackEvent]);

  useContentHeight(contentWrapperRef, [headerWrapperRef], {
    offset: 73,
    useWindowHeight: true,
    deps: [isLoading],
  });

  useEffect(() => {
    handleAnalyzeThread();

    return () => {
      currentAnalyzeRequestRef.current?.abort();
      currentAnalyzeRequestRef.current = null;
    };
  }, [handleAnalyzeThread]);

  useEffect(() => {
    if (!isComposeMode && threadId && threadTrackStatus === null) {
      dispatch(getThreadTrackStatus({ threadId }));
    }
  }, [isComposeMode, threadId, threadTrackStatus, dispatch]);

  if (isComposeMode) {
    return <EmptyScreen {...attrs.emptyScreen} />;
  }

  return (
    <div {...attrs.container}>
      <div {...attrs.headerWrapper}>
        <Header {...attrs.header} />
        <AuthorityZone {...attrs.authorityZone} />
        <div {...attrs.tabsWrapper}>
          <Tabs {...attrs.tabs} />
        </div>
      </div>
      <div {...attrs.contentWrapper}>
        <Scrollbar {...attrs.scrollbar}>
          <div {...attrs.content}>{renderActiveTabContent()}</div>
        </Scrollbar>
      </div>
      <ErrorOverlay {...attrs.errorOverlay} />
    </div>
  );
};

export default withTransition(AnalysisScreen);
